import { SEARCH_INDEX, type SearchEntry } from "../data/searchIndex";
import { getOrderByReference, type OrderStatus } from "../utils/firebaseClient";
import { formatInr } from "../utils/priceFormat";

export interface ChatbotAnswer {
  confident: boolean;
  text: string;
  linkPath?: string;
  linkLabel?: string;
}

// ZP-ORD-2026-00012345 — same shape generateOrderReference() produces.
// Anchored with \b on both ends: unanchored, a mistyped reference with an
// extra trailing digit would still "match" its first 8 digits and silently
// look up the wrong order instead of falling through to the not-found path.
export const ORDER_REFERENCE_PATTERN = /\bZP-ORD-\d{4}-\d{8}\b/i;

// "reported, not verified" for payment_reported_unverified — must never say
// "paid"/"confirmed" (see Checkout.tsx's identical STATUS_COPY, which this
// has to stay honest alongside).
const ORDER_STATUS_COPY: Record<OrderStatus, string> = {
  payment_reported_unverified: "Your payment was reported by Razorpay, but hasn't been verified yet — our team is confirming it manually.",
  payment_failed: "This order shows a failed payment, so it wasn't placed.",
  payment_cancelled: "This order shows a cancelled payment, so it wasn't placed.",
};

const CONFIDENCE_THRESHOLD = 50;

// Reuses SEARCH_INDEX (ZP-95's global search corpus — the same real
// site content: page descriptions, delivery cities, FAQ answers) instead of
// building a second parallel index. This IS the "grounded on published
// content, never fabricates" mechanism the AC asks for — there's no LLM
// here to hallucinate past what's in this array.
function scoreEntry(entry: SearchEntry, q: string): number {
  const title = entry.title.toLowerCase();
  if (title === q) return 100;
  if (title.startsWith(q)) return 80;
  if (title.includes(q)) return 60;
  if (entry.keywords.some((k) => k.startsWith(q))) return 55;
  if (entry.keywords.some((k) => k.includes(q))) return 40;
  if (entry.subtitle?.toLowerCase().includes(q)) return 35;
  // Trailing punctuation ("certifications?") previously prevented a word
  // from ever matching a keyword, even when the underlying word was a
  // perfect match — stripped here rather than just at the top of `ask()`
  // so a query like "sustainability certifications?" scores the same as
  // "sustainability certifications".
  const qWords = q
    .split(/\s+/)
    .map((w) => w.replace(/^[^\w]+|[^\w]+$/g, ""))
    .filter((w) => w.length > 2);
  const wordHits = qWords.filter(
    (w) => title.includes(w) || entry.keywords.some((k) => k.includes(w))
  ).length;
  if (qWords.length > 0 && wordHits > 0) {
    const ratio = wordHits / qWords.length;
    // A perfect word-overlap match (every meaningful word in the query
    // hits something) gets a bonus that can cross CONFIDENCE_THRESHOLD —
    // otherwise this whole scoring tier tops out at 45, structurally below
    // the 50-point threshold, meaning no query landing here could ever be
    // treated as confident no matter how good the match actually was.
    const perfectMatchBonus = qWords.length > 1 && wordHits === qWords.length ? 10 : 0;
    return Math.round(ratio * 45) + perfectMatchBonus;
  }
  return 0;
}

/** Deterministic, local, zero-latency FAQ/content matcher — see the module comment above for why this stands in for the ticket's stated LLM backend. */
export default function useChatbotEngine() {
  const ask = (question: string): ChatbotAnswer => {
    const q = question.trim().toLowerCase();
    if (!q) {
      return { confident: false, text: "Go ahead and type a question — I'm listening." };
    }

    const scored = SEARCH_INDEX.map((entry) => ({ entry, score: scoreEntry(entry, q) }))
      .filter((r) => r.score > 0)
      .sort((a, b) => b.score - a.score);

    const best = scored[0];
    if (!best || best.score < CONFIDENCE_THRESHOLD) {
      return {
        confident: false,
        text: "I'm not confident I can answer that accurately from our published info.",
      };
    }

    if (best.entry.category === "FAQ") {
      return {
        confident: true,
        text: best.entry.subtitle ?? best.entry.title,
        linkPath: best.entry.path,
        linkLabel: "More on our Contact page",
      };
    }

    return {
      confident: true,
      text: best.entry.subtitle
        ? `${best.entry.subtitle} — see "${best.entry.title}" for the full details.`
        : `You'll find that under "${best.entry.title}".`,
      linkPath: best.entry.path,
      linkLabel: `Go to ${best.entry.title}`,
    };
  };

  /**
   * A live Firestore read, not the static local matcher above — real order
   * data has no place in a static SEARCH_INDEX. Never throws (see
   * getOrderByReference); a not-found order gets one honest answer rather
   * than a stack trace or a silent fallback to the generic "not confident"
   * message, which would be misleading for something the visitor gave us
   * an exact ID for.
   */
  const lookupOrderStatus = async (orderRef: string): Promise<ChatbotAnswer> => {
    const order = await getOrderByReference(orderRef.toUpperCase());
    if (!order) {
      return {
        confident: false,
        text: `I couldn't find an order matching ${orderRef} — please double-check the reference number.`,
      };
    }
    return {
      confident: true,
      text: `Order ${order.orderRef} (total ${formatInr(order.total)}): ${ORDER_STATUS_COPY[order.status]}`,
    };
  };

  return { ask, lookupOrderStatus };
}
